import moment from 'moment';
import { ActivityItem, ActivityPeriod, AdminUsers } from 'Interfaces/Admin';

/**
 *  Turns the flat activity counts into the grid the Activity view shows: one row
 *  per user, one column per period between the account's first log entry and
 *  today. Ported from ActivityView.getData.
 *
 *  The column keys are defined here and nowhere else, so the arithmetic — the
 *  ISO week rollover at 53 and the month rollover at 13 — is kept verbatim.
 */

export interface ActivityRow {
  username: string;
  [period: string]: string | number;
}

export interface ActivityTable {
  /** Period column keys, in chronological order. */
  columns: string[];
  rows: ActivityRow[];
}

interface PeriodCursor {
  /** How many columns to emit. */
  count: number;
  /** Key for the nth column, advancing the cursor. */
  next: () => string;
}

const cursorFor = (
  period: ActivityPeriod, from: moment.Moment, to: moment.Moment,
): PeriodCursor => {
  let year = from.year();

  if (period === 'week') {
    let week = from.isoWeek();
    return {
      count: Math.ceil(to.diff(from, 'week', true)),
      next: () => {
        const key = `${week}-${year}`;
        week += 1;
        if (week === 53) {
          week = 1;
          year += 1;
        }
        return key;
      },
    };
  }

  if (period === 'month') {
    let month = from.month() + 1;
    return {
      count: Math.ceil(to.diff(from, 'month', true)),
      next: () => {
        const key = `${month}-${year}`;
        month += 1;
        if (month === 13) {
          month = 1;
          year += 1;
        }
        return key;
      },
    };
  }

  return {
    count: Math.ceil(to.diff(from, 'year', true)),
    next: () => {
      const key = `${year}`;
      year += 1;
      return key;
    },
  };
};

/** Finds the count for a user in the period a column key represents. */
const countFor = (
  items: ActivityItem[], userId: number, period: ActivityPeriod, key: string,
): number => {
  const [first, second] = key.split('-').map(Number);
  const match = items.find((item) => {
    if (item.userId !== userId) {
      return false;
    }
    if (period === 'year') {
      return item.year === first;
    }
    const bucket = period === 'week' ? item.week : item.month;
    return bucket === first && item.year === second;
  });
  return match ? match.count : 0;
};

export const buildActivityTable = (
  items: ActivityItem[],
  users: AdminUsers,
  period: ActivityPeriod,
  logDate: string | null,
  now: Date = new Date(),
): ActivityTable => {
  if (!logDate || users.length === 0) {
    return { columns: [], rows: [] };
  }

  const from = moment(logDate);
  const to = moment(now);

  const { count, next } = cursorFor(period, from, to);
  const columns: string[] = [];
  for (let i = 0; i <= count; i += 1) {
    columns.push(next());
  }

  const rows = users
    .map((user) => {
      const row: ActivityRow = { username: user.userName };
      columns.forEach((key) => {
        row[key] = countFor(items, user.id, period, key);
      });
      return row;
    })
    // A user with nothing recorded in any period is left out entirely.
    .filter((row) => columns.some((key) => (row[key] as number) > 0));

  return { columns, rows };
};

/** Column totals, shown as the aggregate row under the grid. */
export const columnTotals = (table: ActivityTable): Record<string, number> =>
  table.columns.reduce((totals, key) => ({
    ...totals,
    [key]: table.rows.reduce((sum, row) => sum + (row[key] as number), 0),
  }), {});
